/** Composition root: wires HTTP, auth, telemetry, notifications, and local feature use cases. */
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import type { HttpTransportPort } from '@core/domain/ports/driven/HttpTransportPort.ts';
import type { HttpClient } from '@core/infrastructure/driven/http/HttpClient.ts';
import { AuthService } from '@src/modules/auth/application/AuthService.ts';
import { AuthStorageAdapter } from '@src/modules/auth/infrastructure/driven/AuthStorageAdapter.ts';
import type { LocalSqliteRuntime } from '@src/modules/shared/local-sqlite/domain/LocalSqliteRuntime.ts';
import { PuntosEntregaApplicationService } from '@src/modules/puntos_entrega/application/PuntosEntregaApplicationService.ts';
import type { RedencionBonoApplicationService } from '@src/modules/redencion_bono/application/RedencionBonoApplicationService.ts';
import type { NotificationPort } from '@src/modules/shared/notifications/domain/ports/NotificationPort.ts';
import type { ObservabilidadPersistedReadPort } from '@src/modules/observabilidad/domain/ports/ObservabilidadPersistedReadPort.ts';
import type { CatalogDownloadPort } from '@src/modules/sync/domain/ports/CatalogDownloadPort.ts';
import type { OfflineSyncPort } from '@src/modules/sync/domain/ports/OfflineSyncPort.ts';
import type { SyncStorageMetricsPort } from '@src/modules/sync/domain/ports/SyncStorageMetricsPort.ts';
import type { AppMaintenancePort } from '@src/modules/sync/domain/ports/AppMaintenancePort.ts';
import type { AppUpdatePort } from '@src/modules/sync/domain/ports/AppUpdatePort.ts';
import { AppContainerBuilder } from './AppContainerBuilder.ts';

export type AppContainerDeps = {
  localSqlite?: LocalSqliteRuntime;
  puntosEntregaShell?: PuntosEntregaApplicationService;
  redencionBonoApplication?: RedencionBonoApplicationService;
};

export class AppContainer {
  /** Only in builds with remote API (`USE_LOCAL_PUNTOS_ENTREGA=0`); no HTTP client in local SQLite mode. */
  private readonly httpClient: HttpClient | null;
  private readonly httpTransport: HttpTransportPort;
  private readonly authStorage: AuthStorageAdapter;
  private readonly authService: AuthService;
  private readonly notificationPort: NotificationPort;
  private readonly telemetry: TelemetryPort;
  private readonly puntosEntregaShell: PuntosEntregaApplicationService;
  private readonly redencionBonoApplication: RedencionBonoApplicationService;
  private readonly syncStorageMetrics: SyncStorageMetricsPort;
  private readonly offlineSync: OfflineSyncPort;
  private readonly catalogDownload: CatalogDownloadPort;
  private readonly observabilidadPersistedRead: ObservabilidadPersistedReadPort;
  private readonly appMaintenance: AppMaintenancePort;
  private readonly appUpdate: AppUpdatePort;

  /** Initializes the application container with necessary dependencies and configurations. */
  constructor(deps: AppContainerDeps = {}) {
    const assembled = new AppContainerBuilder(deps).assemble();
    this.httpClient = assembled.httpClient;
    this.httpTransport = assembled.httpTransport;
    this.authStorage = assembled.authStorage;
    this.authService = assembled.authService;
    this.notificationPort = assembled.notificationPort;
    this.telemetry = assembled.telemetry;
    this.puntosEntregaShell = assembled.puntosEntregaShell;
    this.redencionBonoApplication = assembled.redencionBonoApplication;
    this.syncStorageMetrics = assembled.syncStorageMetrics;
    this.offlineSync = assembled.offlineSync;
    this.catalogDownload = assembled.catalogDownload;
    this.observabilidadPersistedRead = assembled.observabilidadPersistedRead;
    this.appMaintenance = assembled.appMaintenance;
    this.appUpdate = assembled.appUpdate;
  }

  /** Preferred entry for the composition root (delegates to {@link AppContainerBuilder}). */
  static create(deps: AppContainerDeps = {}): AppContainer {
    return new AppContainer(deps);
  }

  /** Retrieves the authentication application service instance. */
  getAuthService(): AuthService {
    return this.authService;
  }

  /** Retrieves the notification port for displaying UI messages. */
  getNotificationPort(): NotificationPort {
    return this.notificationPort;
  }

  /** Retrieves the telemetry port for logging and metrics. */
  getTelemetry(): TelemetryPort {
    return this.telemetry;
  }

  /** Shared low-level transport (XHR) for catalog download and offline sync POST. */
  getHttpTransport(): HttpTransportPort {
    return this.httpTransport;
  }

  /** Retrieves the HTTP client, throwing an error if unavailable in offline mode. */
  getHttpClient(): HttpClient {
    if (!this.httpClient) {
      throw new Error('HTTP no disponible: la app está en modo SQLite local (login y datos sin API).');
    }
    return this.httpClient;
  }

  /** Retrieves the delivery points application service instance. */
  getPuntosEntregaShell(): PuntosEntregaApplicationService {
    return this.puntosEntregaShell;
  }

  /** Retrieves the voucher redemption application service instance. */
  getRedencionBonoApplicationService(): RedencionBonoApplicationService {
    return this.redencionBonoApplication;
  }

  /** Retrieves the storage metrics port for the synchronization page. */
  getSyncStorageMetrics(): SyncStorageMetricsPort {
    return this.syncStorageMetrics;
  }

  /** Offline JSON export POST (`SYNC_UPLOAD_BASE_URL` + `SYNC_UPLOAD_PATH`). */
  getOfflineSync(): OfflineSyncPort {
    return this.offlineSync;
  }

  /** Master catalog refresh GET (`SYNC_DOWNLOAD_BASE_URL` + `SYNC_DOWNLOAD_PATH`). */
  getCatalogDownload(): CatalogDownloadPort {
    return this.catalogDownload;
  }

  /** Admin-only persisted telemetry reader (empty when local DB is unavailable). */
  getObservabilidadPersistedRead(): ObservabilidadPersistedReadPort {
    return this.observabilidadPersistedRead;
  }

  /** Cache wipe and optional local SQLite reset (sync maintenance UI). */
  getAppMaintenance(): AppMaintenancePort {
    return this.appMaintenance;
  }

  /** OTA APK manifest check and sideload install (Android native). */
  getAppUpdate(): AppUpdatePort {
    return this.appUpdate;
  }
}
